<script setup lang="ts">
import Select from '@/components/ui/select/Select.vue';
import SelectTrigger from '@/components/ui/select/SelectTrigger.vue';
import SelectValue from '@/components/ui/select/SelectValue.vue';
import SelectContent from '@/components/ui/select/SelectContent.vue';
import SelectItem from '@/components/ui/select/SelectItem.vue';
import SelectGroup from '@/components/ui/select/SelectGroup.vue';
import SelectLabel from '@/components/ui/select/SelectLabel.vue';
import { computed, type Component, type FunctionalComponent } from 'vue';

const {
  options,
  optionLabel = 'label',
  optionValue = 'value',
  placeholder = 'Select an option',
  size = 'default',
  optionIcon,
  optionIndent,
  triggerLabel,
  optionGroup,
} = defineProps<{
  options?: any[];
  optionLabel?: string;
  optionValue?: string;
  placeholder?: string;
  /**
   * Density preset. `sm` shrinks the trigger to `h-8` and the dropdown items
   * to `text-xs` so the typography matches compact callers (e.g. the filter
   * builder popover). Default keeps the existing `h-9` / `text-sm` look.
   */
  size?: 'sm' | 'default';
  /**
   * Optional per-option icon. Given an option, return a component to draw
   * before its label, or null for none. Callers that have no icons pass
   * nothing and the select renders exactly as before.
   */
  optionIcon?: (option: any) => Component | null;
  /**
   * Optional. Return true to draw an option as a sub-row of the option above it.
   * It is indented so its icon starts where the other options' labels start.
   */
  optionIndent?: (option: any) => boolean;
  /**
   * Optional. The trigger's text for the chosen option, when it should say more
   * than the option's own label (a sub-row's short label needs its parent's name).
   */
  triggerLabel?: (option: any) => string;
  /**
   * Optional. Return the heading an option is listed under. Options with the same
   * heading are drawn together, and the headings keep the order in which they first
   * appear. The filter builder uses it to list "Custom List" and "Schedule" columns
   * apart. Callers that do not pass it get the flat list.
   */
  optionGroup?: (option: any) => string;
}>();

const modelValue = defineModel<any>();

/**
 * Optional. Whether the dropdown is open, for a caller that opens it from code (the filter
 * builder opens a new row's column list). Callers that leave it unbound get the usual
 * open-on-click behaviour.
 */
const open = defineModel<boolean>('open');

/** The chosen option, so the trigger can show its icon alongside the label. */
const selectedOption = computed(() =>
  options?.find((option) => option[optionValue] === modelValue.value) ?? null
);

/**
 * The options split by heading. Without `optionGroup` it is one section with no heading, so the
 * template draws every option the same way in both cases.
 */
const optionSections = computed(() => {
  if (!optionGroup) return [{ heading: null as string | null, options: options ?? [] }];
  const sections: { heading: string | null; options: any[] }[] = [];
  for (const option of options ?? []) {
    const heading = optionGroup(option);
    const section = sections.find((existing) => existing.heading === heading);
    if (section) {
      section.options.push(option);
    } else {
      sections.push({ heading, options: [option] });
    }
  }
  return sections;
});

/** Draws its options with no wrapper, for the one section of a select without headings. */
const UngroupedSection: FunctionalComponent = (_props, { slots }) => slots.default?.();

/**
 * The left padding of one option. An option under a heading is indented past the heading so it
 * reads as one of its sub-items. A sub-row (`optionIndent`) is indented one icon further again.
 * A flat select keeps the item's own padding.
 */
function optionPaddingClass(option: any, grouped: boolean): string {
  const indented = Boolean(optionIndent && optionIndent(option));
  if (grouped) return indented ? 'pl-12' : 'pl-6';
  return indented ? 'pl-8' : '';
}

const selectedIcon = computed(() =>
  selectedOption.value && optionIcon ? optionIcon(selectedOption.value) : null
);

const selectedText = computed(() =>
  selectedOption.value && triggerLabel ? triggerLabel(selectedOption.value) : null
);
</script>

<template>
    <Select v-model="modelValue" v-model:open="open">
      <SelectTrigger v-bind="$attrs" :size="size">
        <component
          :is="selectedIcon"
          v-if="selectedIcon"
          class="size-4 shrink-0"
          aria-hidden="true"
          data-testid="select-trigger-icon"
        />
        <SelectValue :placeholder="placeholder">
          <template v-if="selectedText !== null">{{ selectedText }}</template>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <component
          :is="section.heading === null ? UngroupedSection : SelectGroup"
          v-for="section in optionSections"
          :key="section.heading ?? ''"
        >
          <SelectLabel
            v-if="section.heading !== null"
            :class="size === 'sm' ? 'text-xs py-1 text-muted-foreground' : 'text-muted-foreground'"
          >
            {{ section.heading }}
          </SelectLabel>
          <SelectItem
            v-for="option in section.options"
            :key="option[optionValue]"
            :value="option[optionValue]"
            :class="[size === 'sm' ? 'text-xs py-1' : '', optionPaddingClass(option, section.heading !== null)]"
            :data-indented="optionIndent && optionIndent(option) ? 'true' : undefined"
          >
            <span class="flex min-w-0 items-center gap-2">
              <component
                :is="optionIcon(option)"
                v-if="optionIcon && optionIcon(option)"
                class="size-4 shrink-0"
                aria-hidden="true"
                :data-testid="`select-option-icon-${option[optionValue]}`"
              />
              <span class="truncate">{{ option[optionLabel] }}</span>
            </span>
          </SelectItem>
        </component>
      </SelectContent>
    </Select>
</template>